import { PRACTICE_OTP, PRACTICE_FAMILY_INVITE_CODE } from "@utils/practice/practiceMode";

/** 每個步驟的教學腳本：客戶會看到什麼、客服可以怎麼說、常見問題。 */
export interface StepGuide {
  /** 步驟標題 */
  title: string;
  /** 客戶此刻在畫面上看到什麼 */
  customerSees: string;
  /** 客服可以照著念/引導客戶的話 */
  sayThis: string[];
  /** 常見問題與應對 */
  pitfalls: string[];
  /**
   * 「跟著做」模式的單一大字指令（一步一個動作，口語、給不熟手機的客服看）。
   * 未填時：有聚光燈目標 → 自動用「請按亮起來的『XXX』」；沒有 → 退回 customerSees。
   */
  action?: string;
}

export type StepKey =
  | "inviteCode"
  | "verifyPhone"
  | "profile"
  | "home"
  | "homeOrder"
  | "accountHome"
  | "accountDetail"
  | "familyJoin"
  | "getInvite"
  | "orderProducts"
  | "orderInstall"
  | "orderTime"
  | "orderCart"
  | "orderDone"
  | "lineNotice"
  | "noticeHome"
  | "kioskHome"
  | "kioskCart"
  | "kioskConfirm"
  | "kioskDone"
  | "onbQr"
  | "kioskJoin"
  | "kioskHomeNew"
  | "firstJoinHome"
  | "ordersList"
  | "orderDetail"
  | "accountManage"
  | "accountPayment"
  | "accountCoupons"
  | "smsInvite"
  | "done"
  | "other";

/** 依目前路由 + 是否完成，判斷在哪一個教學步驟。 */
export const getStepKey = (pathname: string, isDone: boolean): StepKey => {
  if (isDone) return "done";
  // 新客戶引導
  if (pathname.includes("/onboarding")) return "onbQr";
  if (pathname.includes("/sms-invite")) return "smsInvite";
  if (pathname.includes("/kiosk/join")) return "kioskJoin";
  // 到期通知：模擬 LINE 聊天室（獨立路由）
  if (pathname.startsWith("/practice-notice")) return "lineNotice";
  // 下單流程（gasShop）
  if (pathname.includes("/gasShop/products")) return "orderProducts";
  if (pathname.includes("/gasShop/installPosition")) return "orderInstall";
  if (pathname.includes("/gasShop/deliveryTime")) return "orderTime";
  if (pathname.includes("/gasShop/cart")) return "orderCart";
  // 訂單查詢：/orders/:id 明細；/orders 清單
  if (/^\/orders\/.+/.test(pathname)) return "orderDetail";
  if (pathname.startsWith("/orders")) return "ordersList";
  // 下單流程（kiosk）
  if (pathname.includes("/kiosk/cart")) return "kioskCart";
  if (pathname.includes("/kiosk/confirm")) return "kioskConfirm";
  if (pathname.includes("/kiosk/success")) return "kioskDone";
  if (pathname === "/kiosk") return "kioskHome";
  // 註冊綁定
  if (pathname.includes("inviteCode")) return "inviteCode";
  if (pathname.includes("verifyPhone")) return "verifyPhone";
  if (pathname.includes("/register/profile")) return "profile";
  if (pathname.includes("/account/addAccount")) return "familyJoin";
  if (pathname.includes("/invite")) return "getInvite";
  if (pathname.includes("/account/detail")) return "accountDetail";
  if (pathname.includes("/account/paymentMethod")) return "accountPayment";
  if (pathname.includes("/account/coupons")) return "accountCoupons";
  if (pathname.startsWith("/account")) return "accountHome"; // manageAccount 情境於 CoachPanel remap 成 accountManage
  if (pathname === "/" || pathname.startsWith("/home")) return "home";
  return "other";
};

export const STEP_GUIDES: Record<Exclude<StepKey, "other">, StepGuide> = {
  inviteCode: {
    title: "步驟 1／4：客戶序號",
    customerSees: "輸入瓦斯行提供的客戶序號。",
    sayThis: ["輸入 6 碼會自動進下一步。", `練習用：隨便輸 6 碼。`],
    pitfalls: [],
    action: "在格子裡輸入 6 個字（練習隨便打 6 個），會自動到下一步。",
  },
  verifyPhone: {
    title: "步驟 2／4：手機驗證",
    customerSees: "輸入手機 → 取得驗證碼 → 輸入 5 碼。",
    sayThis: [`練習用驗證碼：${PRACTICE_OTP}。`],
    pitfalls: ["主要電話之後須與此號碼相同。"],
    action: `先打手機號碼、按「取得驗證碼」，再輸入 ${PRACTICE_OTP}。`,
  },
  profile: {
    title: "步驟 3／4：基本資料",
    customerSees: "姓名/性別/生日/電話/地址/發票，勾同意後完成註冊。",
    sayThis: ["姓名已自動帶入，可修改。", "地址搜尋後從清單挑選。"],
    pitfalls: ["主要電話須等於驗證過的手機。"],
    action: "把資料填一填，勾「同意」，再按最下面的完成。",
  },
  home: {
    title: "首頁",
    customerSees: "App 首頁，下方有「首頁／瓦斯訂購／訂單／帳號」分頁。",
    sayThis: ["請點右下角的「帳號」分頁。"],
    pitfalls: [],
  },
  accountHome: {
    title: "帳號頁",
    customerSees: "「我的瓦斯帳戶」清單，上方有「新增」。",
    sayThis: ["加入別人的帳戶 → 點「新增」。", "自己當邀請人 → 點要邀請的帳戶卡片。"],
    pitfalls: [],
  },
  accountDetail: {
    title: "帳戶詳情",
    customerSees: "帳戶資訊與選項，含「邀請成員」。",
    sayThis: ["點「邀請成員」進入產生邀請碼。"],
    pitfalls: ["只有帳戶負責人能產生邀請碼。"],
  },
  familyJoin: {
    title: "加入家庭：輸入邀請碼",
    action: `選「加入既有的瓦斯帳戶」，填入 6 碼邀請碼（練習碼 ${PRACTICE_FAMILY_INVITE_CODE}），再按「加入」。`,
    customerSees: "在『新增瓦斯帳戶』選「加入既有的瓦斯帳戶」，輸入 6 碼邀請碼後按加入。",
    sayThis: ["邀請碼由邀請人在「帳戶 → 邀請成員」取得。", `練習用邀請碼：${PRACTICE_FAMILY_INVITE_CODE}。`],
    pitfalls: [],
  },
  getInvite: {
    title: "取得邀請碼",
    customerSees: "「你的 6 位數邀請碼」，按「產生邀請碼」後顯示碼與 QR，可複製/分享。",
    sayThis: ["按「產生邀請碼」即可，把碼或連結給對方。", `練習會產生：${PRACTICE_FAMILY_INVITE_CODE}。`],
    pitfalls: [],
  },
  homeOrder: {
    title: "首頁",
    customerSees: "App 首頁，下方有「首頁／瓦斯訂購／訂單／帳號」分頁。",
    sayThis: ["點下方「瓦斯訂購」分頁開始下單。"],
    pitfalls: [],
  },
  // --- 下單流程：瓦斯訂購 gasShop ---
  orderProducts: {
    title: "選擇商品",
    customerSees: "瓦斯商品清單。點商品設定安裝位置後回此頁，全部設定好按「結帳」。",
    sayThis: ["點要訂的瓦斯 → 設定安裝位置 → 結帳。"],
    pitfalls: ["每個品項都要設定安裝位置才能結帳。"],
  },
  orderInstall: {
    title: "安裝位置",
    customerSees: "選使用位置、樓層、是否有電梯、數量，按「加入購物車」。",
    sayThis: ["填好後按「加入購物車」。"],
    pitfalls: [],
  },
  orderTime: {
    title: "配送時間",
    customerSees: "選「立即配送」或「預約配送」。預約要先點日期、再點時段。",
    sayThis: ["立即配送：直接按下一步。", "預約配送：點日期 → 點時段 → 下一步。"],
    pitfalls: ["預約沒選好日期與時段不能下一步。"],
    action: "選好時間（要預約就先點日期再點時段），再按下一步。",
  },
  orderCart: {
    title: "購物車 / 結帳",
    customerSees: "確認地址、電話、發票、付款方式，按「確認下單」。",
    sayThis: ["確認資料後按「確認下單」。"],
    pitfalls: ["瓦斯行未營業時無法送出。"],
  },
  orderDone: {
    title: "訂單已送出",
    customerSees: "訂單詳情頁。",
    sayThis: ["可在「訂單」分頁查看進度。"],
    pitfalls: [],
    action: "下單完成！這頁就是訂單明細。",
  },
  // --- 即將到期通知（模擬 LINE 聊天室）---
  lineNotice: {
    title: "LINE 收到「瓦斯快用完」提醒",
    customerSees: "LINE 聊天室裡瓦斯行推來一張提醒卡：預估用完的日期區間、上次叫桶、建議叫桶日，下面有兩顆按鈕。",
    sayThis: [
      "跟客戶說：這張卡是系統依他的用氣速度自動推的，不用自己記日子。",
      "要馬上訂 → 按「立即叫瓦斯」，會直接進一鍵下單。",
      "想先看還能撐幾天 → 按「查看使用天數」看首頁的預估區間（跟卡片上是同一組日期）。",
      "想再看一次跳出來的樣子，按右上角的「🔁 重播」。",
    ],
    pitfalls: [
      "這是練習站的模擬畫面，不會真的發 LINE 訊息給任何人。",
      "客戶說沒收到通知：多半是封鎖了官方帳號或關掉通知，請他到 LINE 的聊天列表確認。",
    ],
    action: "按卡片上的「立即叫瓦斯」，接著就是一般的一鍵下單。",
  },
  noticeHome: {
    title: "首頁：使用天數",
    customerSees: "首頁的預估用完區間與距離建議叫桶還有幾天，跟剛剛通知卡上的日期是同一組。",
    sayThis: [
      "跟客戶說：卡片上的日期就是這裡算出來的，兩邊一定一樣。",
      "要下單可以直接按首頁的「立即預約叫桶」，不必回 LINE 找那張卡。",
    ],
    pitfalls: [],
    action: "看完預估天數，按「立即預約叫桶」就能下單（或按返回回到那張通知卡）。",
  },
  // --- 下單流程：一鍵下單 kiosk ---
  kioskHome: {
    title: "一鍵下單首頁",
    action: "有「重新下訂上次的瓦斯」就按它最快；沒有的話，直接選一款瓦斯開始下單。",
    customerSees: "可重訂上次或選購商品；右下角浮動購物車。",
    sayThis: ["若上方有「重新下訂上次的瓦斯」，按它最快；否則選一款瓦斯即可。"],
    pitfalls: [],
  },
  kioskCart: {
    title: "購物車",
    customerSees: "確認品項，按「前往結帳」並依引導設定時段/付款方式/發票。",
    sayThis: ["按「前往結帳」。"],
    pitfalls: [],
  },
  kioskConfirm: {
    title: "確認訂單",
    customerSees: "檢視時段、付款方式、發票、金額，按「確認送出」。",
    sayThis: ["確認後按「確認送出」。"],
    pitfalls: [],
    action: "看一下沒問題，按最下面的「確認送出」。",
  },
  kioskDone: {
    title: "下單完成",
    customerSees: "顯示訂單編號與配送資訊。",
    sayThis: ["可按「查看訂單」。"],
    pitfalls: [],
    action: "完成了！這樣就下單好了。",
  },
  done: {
    title: "完成",
    customerSees: "正式環境會導回 LINE 或首頁。",
    sayThis: ["可按「再練一次」重跑。"],
    pitfalls: [],
  },
  // --- 新客戶引導：QR 掃碼 onboarding ---
  onbQr: {
    title: "掃碼註冊（新客戶）",
    customerSees: "掃鋼瓶頸部 QR / 或點示範樣本；接著允許定位，逐層比對身分（條碼→手機→市話→地址）。",
    sayThis: [
      "手機會直接跳出系統的定位詢問 → 按允許可自動帶入送達地址（會自動掃到一支鋼瓶）。",
      "只有先前拒絕過定位的人，才會看到我們自己的說明卡（系統那關已經不會再問了）。",
      "比對到 → 身分卡按「這是我」直接前往下單；按「這不是我」會改用手機＋驗證碼重新確認。",
      "驗證碼練習用 12345。",
      "練習比對值：手機 0916375136、市話 0412345678、地址含「西屯/南屯/大安」會比對到；其他一律查無。",
      "手機比不到 → 再問家用電話 → 地址；都比不到就當這家瓦斯行的新客戶，一樣進一鍵下單。",
    ],
    pitfalls: [
      "這是自成一頁的引導流程，畫面在同一網址內切換。",
      "掃碼入口的瓦斯行由鋼瓶決定，按「這不是我」只是否認客戶身分，不會換掉瓦斯行。",
    ],
    action: "跟著畫面做：先允許定位，再照指示掃碼或輸入手機／市話／地址逐層比對。",
  },
  // --- 新客戶引導：首次加入設定 ---
  firstJoinHome: {
    title: "首次加入設定（首頁入口）",
    customerSees: "首頁橘色「完成加入設定」卡（尚未完成前不顯示「立即預約叫桶」）。",
    sayThis: ["請點橘色「完成加入設定」卡，開始首次設定。"],
    pitfalls: ["未完成設定前無法叫瓦斯，先完成設定。"],
  },
  kioskHomeNew: {
    title: "開始叫瓦斯",
    customerSees: "一鍵下單首頁。剛設好地址的新客戶通常沒有「上次訂購」，直接選一款瓦斯即可。",
    sayThis: ["選一款瓦斯 → 設定安裝位置 → 加入購物車 → 前往結帳。", "若上方有「重新下訂上次的瓦斯」，按它最快。"],
    pitfalls: [],
    action: "選一款瓦斯開始下單（有「重新下訂上次」也可直接按）。",
  },
  kioskJoin: {
    title: "首次加入資料",
    customerSees: "身分核對 →（新客戶）家中鋼瓶/用途 → 鐵牌（選填）→ 配套配件 →「完成加入」。",
    sayThis: [
      "先確認身分：是本人按「是我」，即可直接前往結算。",
      "新客戶：填家中鋼瓶數量與用途、拍鐵牌（選填）、選配件。",
      "都填好後按最下面的「完成加入」即可（合約已暫時移除，不需簽名）。",
    ],
    pitfalls: ["鋼瓶數量/用途未填不能繼續。"],
    action: "照畫面：確認身分 → 填鋼瓶/配件，再按最下面的「完成加入」。",
  },
  // --- 訂單查詢 ---
  ordersList: {
    title: "訂單查詢",
    customerSees: "訂單清單，可切換分頁看不同狀態；點一筆看明細。",
    sayThis: ["點任一筆訂單看配送進度與明細。"],
    pitfalls: [],
    action: "點任何一筆訂單，就能看它的明細。",
  },
  orderDetail: {
    title: "訂單明細",
    customerSees: "訂單編號、狀態、品項、配送地址與時段；剛送出的訂單也在這。",
    sayThis: ["這裡看得到訂單編號與配送進度。"],
    pitfalls: [],
    action: "這頁就是訂單明細，看得到編號和進度。",
  },
  // --- 帳號與地址管理 ---
  accountManage: {
    title: "帳號與地址",
    customerSees: "帳號中心：我的瓦斯帳戶、地址、付款方式、發票、優惠券、錢包。",
    sayThis: [
      "切換/新增送達地址：從地址列進入。",
      "付款方式、發票設定、優惠券都在帳號頁各入口。",
    ],
    pitfalls: ["部分設定僅帳戶負責人可改。"],
    action: "點你要改的項目：地址、付款方式、發票或優惠券。",
  },
  accountPayment: {
    title: "付款方式",
    customerSees: "付款方式清單，可新增/設定預設。",
    sayThis: ["新增或選擇預設付款方式。"],
    pitfalls: [],
    action: "在這裡新增或選一個常用的付款方式。",
  },
  accountCoupons: {
    title: "優惠券",
    customerSees: "可用優惠券清單；結帳時自動套用最划算的。",
    sayThis: ["結帳會自動帶最優惠券，這裡可檢視。"],
    pitfalls: [],
    action: "這裡看得到優惠券，結帳會自動幫你用最划算的。",
  },
  // --- 簡訊邀請落地（靜默註冊直達訂單） ---
  smsInvite: {
    title: "簡訊邀請下單",
    customerSees: "點簡訊裡的邀請連結 → 系統靜默完成註冊 → 直接顯示瓦斯行為你建立的訂單。",
    sayThis: ["這條免填表：客戶點連結就自動註冊並看到訂單。", "畫面會先顯示『正在準備訂單』再進到訂單明細。"],
    pitfalls: ["這條幾乎不需客戶操作，主要示範落地體驗。"],
    action: "等畫面自動完成註冊，會直接帶你到訂單明細。",
  },
};
